import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Shield, Clock, Eye, Bell, Lock, AlertTriangle } from 'lucide-react';

const ParentControls = () => {
  const [settings, setSettings] = useState({
    contentFilters: {
      blockSupportAvA: false,
      blockCoachAvA: false,
      blockGroupParticipation: false
    },
    timeLimit: 120, // minutes per day
    crisisAlerts: true,
    activityMonitoring: true
  });

  const activityData = {
    weeklyTime: [45, 67, 82, 91, 73, 105, 88], // minutes per day
    conversationTopics: ['Worries', 'School Stress', 'Friendships', 'Confidence'],
    moodTrends: [0.6, 0.7, 0.5, 0.8, 0.7, 0.9, 0.8]
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <span>👨‍👩‍👧‍👦</span> Parent Dashboard
            </h1>
            <p className="text-gray-600">Monitor and manage your teen&apos;s HiBFF experience</p>
          </div>
          <div className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold">
            Teen Account: Active
          </div>
        </div>

        {/* Activity Monitoring */}
        <div className="mb-8 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Eye size={24} className="text-blue-500" />
            Activity Monitoring
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-2">This Week&apos;s Usage</p>
              <p className="text-3xl font-bold text-blue-700">8.6 hrs</p>
              <p className="text-xs text-blue-600 mt-1">Avg: 73 min/day</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm font-medium text-purple-900 mb-2">Conversations</p>
              <p className="text-3xl font-bold text-purple-700">24</p>
              <p className="text-xs text-purple-600 mt-1">With 2 AvAs</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-green-900 mb-2">Average Mood</p>
              <p className="text-3xl font-bold text-green-700">7.2/10</p>
              <p className="text-xs text-green-600 mt-1">Trending positive</p>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Conversation Topics (Privacy-Protected)</p>
            <div className="flex flex-wrap gap-2">
              {activityData.conversationTopics.map((topic, i) => (
                <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  {topic}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ℹ️ Topics are categorized, not full transcripts (teen privacy protected)
            </p>
          </div>
        </div>

        {/* Content Filters */}
        <div className="mb-8 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Shield size={24} className="text-red-500" />
            Content Filters
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-700">Block Good Listener AvA</p>
                <p className="text-sm text-gray-500">Prevent access to coaching-focused conversations</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.contentFilters.blockSupportAvA}
                  onChange={(e) => setSettings({
                    ...settings,
                    contentFilters: { ...settings.contentFilters, blockSupportAvA: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-700">Block Group Participation</p>
                <p className="text-sm text-gray-500">Restrict joining support groups</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.contentFilters.blockGroupParticipation}
                  onChange={(e) => setSettings({
                    ...settings,
                    contentFilters: { ...settings.contentFilters, blockGroupParticipation: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Time Limits */}
        <div className="mb-8 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Clock size={24} className="text-orange-500" />
            Daily Time Limit
          </h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Maximum time per day: {settings.timeLimit} minutes
            </label>
            <input
              type="range"
              min="30"
              max="300"
              step="30"
              value={settings.timeLimit}
              onChange={(e) => setSettings({ ...settings, timeLimit: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>30 min</span>
              <span>2.5 hrs</span>
              <span>5 hrs</span>
            </div>
          </div>
        </div>

        {/* Crisis Alerts */}
        <div className="mb-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl">
          <h2 className="text-xl font-semibold text-red-900 mb-4 flex items-center gap-2">
            <AlertTriangle size={24} className="text-red-600" />
            Crisis Alerts
          </h2>
          <p className="text-red-800 mb-4">
            You will be notified immediately if your teen clicks the Crisis Help button or if concerning patterns are detected.
          </p>
          <div className="flex items-center justify-between p-4 bg-white rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Emergency SMS/Email Alerts</p>
              <p className="text-sm text-gray-600">Get instant notifications for crisis situations</p>
            </div>
            <span className="px-4 py-2 bg-green-500 text-white font-semibold rounded-lg">
              Active
            </span>
          </div>
        </div>

        {/* Privacy Transparency */}
        <div className="p-6 bg-purple-50 border-2 border-purple-200 rounded-xl">
          <h2 className="text-xl font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Lock size={24} className="text-purple-600" />
            Privacy & Transparency
          </h2>
          <p className="text-purple-800 mb-4">
            Your teen can see what information you can view. We believe in transparency while maintaining safety.
          </p>
          <ul className="space-y-2 text-sm text-purple-700">
            <li>✅ Teen can request privacy for specific conversations</li>
            <li>✅ Topics are categorized (not full transcripts)</li>
            <li>✅ Mood trends shared with parental consent</li>
            <li>✅ Crisis alerts override privacy settings</li>
          </ul>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default ParentControls;
